import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { globalSearch, GlobalSearchResults } from "../../services/search.service";
import { handlePromise } from "../../utils/handlePromisse";
import useGatedNavigate from "./useGatedNavigate";

const rowSx = {
  px: 1.5,
  py: 0.75,
  cursor: "pointer",
  borderRadius: 1,
  "&:hover": { bgcolor: "action.hover" },
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

export default function GlobalSearch() {
  const { navigateGated, gateDialogs } = useGatedNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GlobalSearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  const runSearch = useCallback(async (q: string) => {
    setLoading(true);
    const [res] = await handlePromise(globalSearch(q));
    setResults(res?.data ?? null);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setResults(null);
      setOpen(false);
      return;
    }
    setOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  const goAndClose = (fn: () => void) => {
    setTimeout(() => {
      fn();
      setQuery("");
      setOpen(false);
    }, 100);
  };

  const hasResults =
    results &&
    ((results.actives?.length ?? 0) > 0 ||
      (results.patients?.length ?? 0) > 0 ||
      (results.consultations?.length ?? 0) > 0);
  const noneFound = !loading && results !== null && !hasResults;

  return (
    <>
      <Box ref={containerRef} sx={{ position: "relative", width: 320 }}>
        <TextField
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKey}
          onFocus={() => query.length >= 2 && setOpen(true)}
          placeholder="Buscar ativos, pacientes…"
          inputProps={{ "aria-label": "Busca global" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {loading ? (
                  <CircularProgress size={16} sx={{ color: "text.disabled" }} />
                ) : (
                  <SearchOutlined sx={{ fontSize: 18, color: "text.disabled" }} />
                )}
              </InputAdornment>
            ),
          }}
          sx={{ width: "100%", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />

        {open && (
          <Paper
            elevation={8}
            sx={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              right: 0,
              zIndex: 1400,
              maxHeight: 360,
              overflowY: "auto",
              p: 1,
            }}
          >
            {noneFound && (
              <Typography sx={{ p: 1.5, color: "text.secondary", fontSize: 13 }}>
                Nada encontrado
              </Typography>
            )}

            {results?.actives?.length ? (
              <Box>
                <Typography
                  variant="overline"
                  sx={{ px: 1.5, color: "text.disabled", fontSize: 10 }}
                >
                  Ativos
                </Typography>
                {results.actives.map((a) => (
                  <Box
                    key={a.id}
                    sx={rowSx}
                    onClick={() =>
                      goAndClose(() =>
                        navigateGated("/dashboard/actives", {
                          openActive: { id: a.id, type: a.type, name: a.name },
                        })
                      )
                    }
                  >
                    <Typography sx={{ fontSize: 13 }}>{a.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                      {a.type}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : null}

            {results?.patients?.length ? (
              <Box>
                <Typography
                  variant="overline"
                  sx={{ px: 1.5, color: "text.disabled", fontSize: 10 }}
                >
                  Pacientes
                </Typography>
                {results.patients.map((p) => (
                  <Box
                    key={p.id}
                    sx={rowSx}
                    onClick={() => goAndClose(() => navigateGated("/dashboard/patients/" + p.id))}
                  >
                    <Typography sx={{ fontSize: 13 }}>{p.name}</Typography>
                  </Box>
                ))}
              </Box>
            ) : null}

            {results?.consultations?.length ? (
              <Box>
                <Typography
                  variant="overline"
                  sx={{ px: 1.5, color: "text.disabled", fontSize: 10 }}
                >
                  Consultas
                </Typography>
                {results.consultations.map((c) => (
                  <Box
                    key={c.id}
                    sx={rowSx}
                    onClick={() =>
                      goAndClose(() =>
                        navigateGated(
                          c.type === "ORAL"
                            ? `/dashboard/reviewOral/${c.id}`
                            : `/dashboard/reviewConsult/${c.id}`
                        )
                      )
                    }
                  >
                    <Typography sx={{ fontSize: 13 }}>{c.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                      {format(new Date(c.date), "d MMM", { locale: ptBR })}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : null}
          </Paper>
        )}
      </Box>
      {gateDialogs}
    </>
  );
}
